/**
 * QMS Permission Management Hooks
 * TanStack Query hooks for managing user QMS permissions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks/useSession';
import {
  fetchUsersWithPermissions,
  updateUserPermissions,
  type QmsUserWithPermissions,
} from '../api/qmsApi';

// Query keys factory
export const qmsPermissionKeys = {
  all: ['qms-permissions'] as const,
  users: (search?: string) => [...qmsPermissionKeys.all, 'users', search] as const,
};

// Available QMS permissions that can be managed
export const QMS_PERMISSIONS = [
  {
    key: 'qmstickets.read',
    labelKey: 'settings.permissions.read',
    descriptionKey: 'settings.permissions.readDescription',
  },
  {
    key: 'qmstickets.qd_approval',
    labelKey: 'settings.permissions.qdApproval',
    descriptionKey: 'settings.permissions.qdApprovalDescription',
  },
  {
    key: 'qmstickets.ceo_review',
    labelKey: 'settings.permissions.ceoReview',
    descriptionKey: 'settings.permissions.ceoReviewDescription',
  },
  {
    key: 'qmstickets.create',
    labelKey: 'settings.permissions.create',
    descriptionKey: 'settings.permissions.createDescription',
  },
  {
    key: 'qmstickets.edit',
    labelKey: 'settings.permissions.edit',
    descriptionKey: 'settings.permissions.editDescription',
  },
  {
    key: 'qmstickets.status',
    labelKey: 'settings.permissions.status',
    descriptionKey: 'settings.permissions.statusDescription',
  },
  {
    key: 'qmstickets.approve_status',
    labelKey: 'settings.permissions.approveStatus',
    descriptionKey: 'settings.permissions.approveStatusDescription',
  },
  {
    key: 'qmstickets.delete',
    labelKey: 'settings.permissions.delete',
    descriptionKey: 'settings.permissions.deleteDescription',
  },
  {
    key: 'qmstickets.assign',
    labelKey: 'settings.permissions.assign',
    descriptionKey: 'settings.permissions.assignDescription',
  },
  {
    key: 'qmstickets.change_control',
    labelKey: 'settings.permissions.changeControl',
    descriptionKey: 'settings.permissions.changeControlDescription',
  },
] as const;

export type QmsPermissionKey = (typeof QMS_PERMISSIONS)[number]['key'];

/**
 * Hook for fetching users with their permissions
 */
export function useUsersWithPermissions(search?: string) {
  return useQuery({
    queryKey: qmsPermissionKeys.users(search),
    queryFn: () => fetchUsersWithPermissions(search),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Mutation variables for updating permissions
 * Includes the specific permission being toggled for UI feedback
 */
export interface UpdatePermissionVariables {
  userId: string;
  permissionStrings: string[];
  permissionKey: string; // The specific permission being toggled
}

/**
 * Hook for updating a user's permissions
 * Automatically invalidates session if current user's permissions are updated
 */
export function useUpdateUserPermissions() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('qms');
  const { user: currentUser, invalidateSession } = useSession();

  return useMutation({
    mutationFn: ({ userId, permissionStrings }: UpdatePermissionVariables) =>
      updateUserPermissions(userId, permissionStrings),
    onMutate: async ({ userId, permissionStrings }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: qmsPermissionKeys.all });

      // Snapshot the previous value for all user queries
      const previousData = queryClient.getQueriesData<QmsUserWithPermissions[]>({
        queryKey: qmsPermissionKeys.all,
      });

      // Optimistically update all matching queries
      queryClient.setQueriesData<QmsUserWithPermissions[]>(
        { queryKey: qmsPermissionKeys.all },
        (old) => {
          if (!old) return old;
          return old.map((user) =>
            user._id === userId
              ? { ...user, permission_strings: permissionStrings }
              : user
          );
        }
      );

      return { previousData };
    },
    onSuccess: (_data, variables) => {
      toast.success(t('settings.permissions.updateSuccess'));

      // If we updated the current user's permissions, invalidate their session
      // so the new permissions take effect immediately
      if (currentUser?._id === variables.userId) {
        invalidateSession();
      }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(t('settings.permissions.updateError'));
    },
    onSettled: () => {
      // Invalidate to refetch
      queryClient.invalidateQueries({ queryKey: qmsPermissionKeys.all });
    },
  });
}

/**
 * Helper to check if a user has a specific QMS permission
 */
export function hasQmsPermission(
  user: QmsUserWithPermissions,
  permission: QmsPermissionKey
): boolean {
  return user.permission_strings.includes(permission);
}

/**
 * Helper to toggle a permission for a user
 * Returns the new permission_strings array
 */
export function togglePermission(
  currentPermissions: string[],
  permission: QmsPermissionKey,
  enabled: boolean
): string[] {
  if (enabled) {
    // Add permission if not already present
    if (!currentPermissions.includes(permission)) {
      return [...currentPermissions, permission];
    }
    return currentPermissions;
  } else {
    // Remove permission
    return currentPermissions.filter((p) => p !== permission);
  }
}
